import { type CartLineItem } from '@lib/entities/Cart';
import { type MenuCategory, type MenuItem } from '@lib/entities/Menu';
import { type Order } from '@lib/entities/Order';
import { type DeliveryType } from '@lib/entities/Store';

type AuthMethod = 'Google' | 'Apple' | 'Email';

interface SignUp {
    name: 'sign_up';
    method: AuthMethod;
}

interface Login {
    name: 'login';
    method: AuthMethod;
}

interface HasItemsWithValue {
    currency: string;
    value: number;
    items: Item[];
}

interface ViewItem extends HasItemsWithValue {
    name: 'view_item';
}

interface ViewItemList {
    name: 'view_item_list';
    item_list_id: string;
    item_list_name: string;
    items: Item[];
}

interface AddToCart extends HasItemsWithValue {
    name: 'add_to_cart';
}

interface ViewCart extends HasItemsWithValue {
    name: 'view_cart';
}

interface RemoveFromCart extends HasItemsWithValue {
    name: 'remove_from_cart';
}

interface AddPaymentInfo extends HasItemsWithValue {
    name: 'add_payment_info';
    payment_type: 'Credit Card';
    coupon?: string;
}

interface Purchase extends HasItemsWithValue {
    name: 'purchase';
    transaction_id: string;
    coupon?: string;
    tax: number;
    /**
     * NB: delivery fee.
     */
    shipping: number;
    store_id: number;
    store_name: string;
    logged_in: boolean;
    bring_back_bowl?: boolean;
    // TODO: update @lib/entities/Store.{DeliveryType => DeliveryMethod} to
    // match this union so that this can be `delivery_method: DeliveryMethod`
    delivery_method: 'delivery' | 'pickup';
}

interface PreviousLocationOrder {
    name: 'previous_location_order';
    store_id: number;
    delivery_method: 'delivery' | 'pickup';
}

interface ChooseLocationOrder {
    name: 'choose_location_order';
}

interface SkipLocation {
    name: 'skip_location';
}

interface Item {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price: number;
    quantity?: number;
}

export type AnalyticsEvent =
    | SignUp
    | Login
    | ViewItem
    | ViewItemList
    | AddToCart
    | ViewCart
    | RemoveFromCart
    | AddPaymentInfo
    | Purchase
    | PreviousLocationOrder
    | ChooseLocationOrder
    | SkipLocation;

export interface AnalyticsPayload<E extends AnalyticsEvent> {
    event: E['name'];
    ecommerce: Omit<E, 'name'>;
}

export interface IdentifyPayload {
    user_id: number;
}

export type DatalayerResetPayload = { ecommerce: null };

type JustSaladItem = MenuItem | CartLineItem;

export const toAnalyticsItem = (jsItem: JustSaladItem, index: number): Item => {
    const isCartItem = 'menuItem' in jsItem;

    const {
        id,
        name: item_name,
        category: item_category,
        price
    } = isCartItem ? jsItem.menuItem : jsItem;

    const base = {
        item_id: id.toString(),
        item_name,
        item_list_name: item_category,
        index,
        item_category,
        price
    };

    if (isCartItem) {
        return {
            ...base,
            quantity: jsItem.quantity
        };
    }

    return base;
};

const toItemsWithValue = (items: JustSaladItem[]): HasItemsWithValue => {
    return {
        currency: 'USD',
        value: items.reduce((sum, item) => sum + item.price, 0),
        items: items.map(toAnalyticsItem)
    };
};

export const toAnalyticsList = (category: MenuCategory, items: MenuItem[]) => {
    const list: Omit<ViewItemList, 'name'> = {
        item_list_id: category.id.toString(),
        item_list_name: category.type,
        items: items.map(toAnalyticsItem)
    };

    return list;
};

export const signUp = (method: AuthMethod): SignUp => {
    const event: SignUp = {
        name: 'sign_up',
        method
    };

    return event;
};

export const login = (method: AuthMethod) => {
    const event: Login = {
        name: 'login',
        method
    };

    return event;
};

export const viewItem = (item: MenuItem) => {
    const event: ViewItem = {
        name: 'view_item',
        ...toItemsWithValue([item])
    };

    return event;
};

export const viewItemList = (category: MenuCategory, items: MenuItem[]) => {
    const event: ViewItemList = {
        name: 'view_item_list',
        item_list_id: category.id.toString(),
        item_list_name: category.type,
        items: items.map(toAnalyticsItem)
    };
    return event;
};

export const addToCart = (item: CartLineItem) => {
    const event: AddToCart = {
        name: 'add_to_cart',
        ...toItemsWithValue([item])
    };

    return event;
};

export const viewCart = (items: CartLineItem[]) => {
    const event: ViewCart = {
        name: 'view_cart',
        ...toItemsWithValue(items)
    };

    return event;
};

export const removeFromCart = (item: CartLineItem) => {
    const event: RemoveFromCart = {
        name: 'remove_from_cart',
        ...toItemsWithValue([item])
    };

    return event;
};

export const addPaymentInfo = (items: CartLineItem[]) => {
    const event: AddPaymentInfo = {
        name: 'add_payment_info',
        payment_type: 'Credit Card',
        ...toItemsWithValue(items)
    };

    return event;
};

export interface PurchaseProps {
    order: Order;
    /**
     * We pass the somewhat redundant items array to this function in
     * order to keep the `category` consistent. Might be able to change
     * this if we ever update the back end with consistent slugs
     */
    items: CartLineItem[];
    promoCode?: string;
    isBringBackBowl: boolean;
    isLoggedIn: boolean;
    deliveryMethod: DeliveryType;
}
export const purchase = ({
    order,
    items,
    promoCode,
    isBringBackBowl,
    isLoggedIn,
    deliveryMethod
}: PurchaseProps) => {
    const event: Purchase = {
        ...toItemsWithValue(items),
        name: 'purchase',
        transaction_id: order.order_id.toString(),
        value: order.subtotal,
        coupon: promoCode,
        tax: order.tax,
        shipping: order.delivery_fee,
        store_id: order.store_id,
        store_name: order.store_name,
        bring_back_bowl: isBringBackBowl,
        logged_in: isLoggedIn,
        // see note in the Purchase interface declaration
        delivery_method: deliveryMethod === 'Pickup' ? 'pickup' : 'delivery'
    };

    return event;
};

export const previousLocationOrder = (storeId: number, deliveryMethod: DeliveryType) => {
    const event: PreviousLocationOrder = {
        name: 'previous_location_order',
        store_id: storeId,
        delivery_method: deliveryMethod === 'Pickup' ? 'pickup' : 'delivery'
    };

    return event;
};

export const chooseLocationOrder = (): ChooseLocationOrder => {
    const event: ChooseLocationOrder = {
        name: 'choose_location_order'
    };

    return event;
};

export const skipLocation = (): SkipLocation => {
    const event: SkipLocation = {
        name: 'skip_location'
    };

    return event;
};

export const toEventPayload = <A extends AnalyticsEvent>(event: A): AnalyticsPayload<A> => {
    const { name, ...payload } = event;

    return {
        event: name,
        ecommerce: payload
    };
};
